import {AbstractControl, DISABLED, INVALID, PENDING, VALID} from './AbstractControl';
import _ from 'lodash';
import {AbstractControlState, ControlOptions, ControlProperties, FormControlState} from '../interfaces/Form';
import {forkJoin, from, Observable} from 'rxjs';
import {Yup} from '../yup';
import {ValidationError} from "yup";


/**
 * Tracks the value and validation status of an individual form control.
 *
 * This is one of the three fundamental building blocks of Angular form, along with
 * `FormGroup.ts` and `FormArray`. It extends the `AzoraAbstractControl` class that
 * implements most of the base functionality for accessing the value, validation status,
 * user interactions and events.
 *
 * @see `AzoraAbstractControl`
 * @see [Reactive Forms Guide](guide/reactive-form)
 * @see [Usage Notes](#usage-notes)
 *
 * @usageNotes
 *
 * ### Initializing Form Controls
 *
 * Instantiate a `FormControl`, with an initial value.
 *
 * ```ts
 * const control = new FormControl('some value');
 * console.log(control.value);     // 'some value'
 *```
 *
 * The following example initializes the control with a form state object. The `value`
 * and `disabled` keys are required in this case.
 *
 * ```ts
 * const control = new FormControl({ value: 'n/a', disabled: true });
 * console.log(control.value);     // 'n/a'
 * console.log(control.status);    // 'DISABLED'
 * ```
 *
 * The following example initializes the control with a sync validator.
 *
 * ```ts
 * const control = new FormControl('', types.required);
 * console.log(control.value);      // ''
 * console.log(control.status);     // 'INVALID'
 * ```
 *
 * The following example initializes the control using an options object.
 *
 * ```ts
 * const control = new FormControl('', {
 *    types: types.required,
 *    asyncValidators: myAsyncValidator
 * });
 * ```
 *
 * ### Configure the control to update on a blur event
 *
 * Set the `updateOn` option to `'blur'` to update on the blur `event`.
 *
 * ```ts
 * const control = new FormControl('', { updateOn: 'blur' });
 * ```
 *
 * ### Configure the control to update on a submit event
 *
 * Set the `updateOn` option to `'submit'` to update on a submit `event`.
 *
 * ```ts
 * const control = new FormControl('', { updateOn: 'submit' });
 * ```
 *
 * ### Reset the control back to an initial value
 *
 * You reset to a specific form state by passing through a standalone
 * value or a form state object that contains both a value and a disabled state
 * (these are the only two properties that cannot be calculated).
 *
 * ```ts
 * const control = new FormControl('Nancy');
 *
 * console.log(control.value); // 'Nancy'
 *
 * control.reset('Drew');
 *
 * console.log(control.value); // 'Drew'
 * ```
 *
 * ### Reset the control back to an initial value and disabled
 *
 * ```
 * const control = new FormControl('Nancy');
 *
 * console.log(control.value); // 'Nancy'
 * console.log(control.status); // 'VALID'
 *
 * control.reset({ value: 'Drew', disabled: true });
 *
 * console.log(control.value); // 'Drew'
 * console.log(control.status); // 'DISABLED'
 * ```
 *
 * @publicApi
 */
export class FormControl extends AbstractControl {
    
    public readonly props: ControlProperties
    
    /**
     * Creates a new `FormControl` instance.
     *
     * @param state Initializes the control with an initial value, or an object that defines the initial value and disabled state.
     *
     * @param options An `ControlOptions` object
     *
     */
    constructor(state?: FormControlState, options?: ControlOptions) {
        super(
                <FormControlState>state,
                options
        );
        // Set the prototype explicitly.
        Object.setPrototypeOf(this, FormControl.prototype);
        
        this._initObservables();
        this._applyControlState();
        this._getControlProperties();
        this.updateValueAndValidity({onlySelf: true, emitEvent: false});
    }
    
    
    /**
     * Sets a new value for the form control.
     *
     * @param value The new value for the control.
     * @param options options options that determine how the control proopagates changes
     * and emits events when the value changes.
     * The options options are passed to the {@link AzoraAbstractControl#updateValueAndValidity
   * updateValueAndValidity} method.
     *
     * * `onlySelf`: When true, each change only affects this control, and not its parent. Default is
     * false.
     * * `emitEvent`: When true or not supplied (the default), both the `statusChanges` and
     * `valueChanges`
     * observables emit events with the latest status and value when the control value is updated.
     * When false, no events are emitted.
     * * `emitModelToViewChange`: When true or not supplied  (the default), each change triggers an
     * `onChange` event to
     * update the view.
     * * `emitViewToModelChange`: When true or not supplied (the default), each change triggers an
     * `ngModelChange`
     * event to update the model.
     *
     */
    setValue(value: any, options: {
        onlySelf?: boolean,
        emitEvent?: boolean
    } = {}): void {
        (this.value === value) ? this.markAsPristine() : this.markAsDirty();
        
        (<{ value: any }>this).value = value;
        
        this.updateValueAndValidity(options);
        
        // if (_.has(this.options, ['submitOnChange'])) {
        //
        //   this.root.submit(true);
        // }
        
    }
    
    
    /**
     * Resets the form control, marking it `pristine` and `untouched`, and setting
     * the value to null.
     *
     * @param formState Resets the control with an initial value,
     * or an object that defines the initial value and disabled state.
     *
     * @param options options options that determine how the control propagates changes
     * and emits events after the value changes.
     *
     * * `onlySelf`: When true, each change only affects this control, and not its parent. Default is
     * false.
     * * `emitEvent`: When true or not supplied (the default), both the `statusChanges` and
     * `valueChanges`
     * observables emit events with the latest status and value when the control is reset.
     * When false, no events are emitted.
     *
     */
    reset(value: any = null, options: { onlySelf?: boolean, emitEvent?: boolean } = {}): void {
        
        this.markAsPristine(options);
        this.markAsUntouched(options);
        this._applyControlState();
        
        
        if (value) {
            (<FormControl>this.value) = value;
        }
        
        this.updateValueAndValidity(options);
        
        // if (_.has(this.options, ['submitOnChange'])) {
        //
        //   this.root.submit(true);
        // }
    }
    
    /**
     * Resets the form control, marking it `pristine` and `untouched`, and setting
     * the value to null.
     *
     * @param formState Resets the control with an initial value,
     * or an object that defines the initial value and disabled state.
     *
     * @param options options options that determine how the control propagates changes
     * and emits events after the value changes.
     *
     * * `onlySelf`: When true, each change only affects this control, and not its parent. Default is
     * false.
     * * `emitEvent`: When true or not supplied (the default), both the `statusChanges` and
     * `valueChanges`
     * observables emit events with the latest status and value when the control is reset.
     * When false, no events are emitted.
     *
     */
    clear(options: { onlySelf?: boolean, emitEvent?: boolean } = {}): void {
        this.markAsPristine(options);
        this.markAsUntouched(options);
        (this as { value: any }).value = null;
        this.updateValueAndValidity(options);
        // if (_.has(this.options, ['submitOnChange'])) {
        //
        //   this.root.submit(true);
        // }
    }
    
    
    /**
     * Sets control state on a form control .
     *
     * Calling `setOptions` also updates the validity of the parent control.
     *
     * @usageNotes
     * ### Manually set the options for a control
     *
     * ```
     * const login = new FormControl('someLogin');
     * login.setErrors({
     *   notUnique: true
     * });
     *
     * expect(login.valid).toEqual(false);
     * expect(login.errors).toEqual({ notUnique: true });
     *
     * login.setValue('someOtherLogin');
     *
     * expect(login.valid).toEqual(true);
     * ```
     */
    setControlState(newState: FormControlState | null, opts: { emitEvent?: boolean } = {}): void {
        this.state = newState;
        this._applyControlState()
        this._getControlProperties()
    }
    
    updateControlState(newState: FormControlState | null, opts: { emitEvent?: boolean } = {}): void {
        this.state = {
            ...this.state,
            ...newState
        }
        this._applyControlState()
        this._getControlProperties()
    }
    
    /** @internal */
    _getControlSchema = () => {
        if ( this.state && (<FormControlState>this.state).schema) return (<FormControlState>this.state).schema;
    };
    
    
    // /**
    //  * Sets the async types that are active on this control. Calling this
    //  * overwrites any existing async types.
    //  */
    // private _setAsyncValidators = (asyncValidators: AsyncValidatorFn | AsyncValidatorFn[] | null): void => {
    //
    //
    //   (this as { asyncValidator: AsyncValidatorFn | null }).asyncValidator = _.isArray(asyncValidators)
    //     ? this._composeAsync(<AsyncValidatorFn[]>asyncValidators)
    //     : <AsyncValidatorFn>asyncValidators || null;
    // };
    //
    // /** @internal */
    // _composeAsync(asyncValidators: AsyncValidatorFn[]): AsyncValidatorFn | null {
    //   if (!asyncValidators) return null;
    //   const presentValidators: AsyncValidatorFn[] = asyncValidators.filter((o: any) => o !== null) as any;
    //   if (presentValidators.length === 0) return null;
    //
    //   const $this = this;
    //   return function(control: AbstractControl): Observable<ValidationError[] | null> {
    //     const observables = $this._executeAsyncValidators(control, presentValidators);
    //
    //     return forkJoin(observables).pipe(map($this._mergeErrors));
    //
    //   };
    // }
    //
    // /** @internal */
    // _mergeErrors(arrayOfErrors: (ValidationError | ValidationError[] | null)[]): ValidationError[] | null {
    //   return _.reduce(arrayOfErrors, (result: ValidationError[] | null, err: ValidationError | ValidationError[] | null) => {
    //     if (err) {
    //       result = result.concat(_.isArray(err) ? <ValidationError[]>err : [<ValidationError>err]);
    //     }
    //     return result;
    //   }, []);
    //
    // }
    
    /** @internal */
    _updateValue(): void {
    
    }
    
    /** @internal */
    
    _runValidator = _.debounce(() => {
        
        if (this.parent && this.parent.schema) {
            from(Yup.reach(this.parent.schema, this.path).validate(this.value)).subscribe(
                    value => {
                    
                    },
                    (err: ValidationError) => {
                        
                        this.setError(err);
                    }
            );
        }
        
        if (!this.parent && this.schema) {
            from(this.schema.validate(this.value)).subscribe(
                    value => {
                    
                    },
                    (err: ValidationError) => {
                        
                        this.setError(err);
                    }
            );
        }
    }, this.debounce)
    
    private _applyControlState = () => {
        
        if (this.state) {
            (<FormControl>this.value) = (<FormControlState>this.state).value || null;
        }
        
        if(this.options) {
            (<{ debounce: number }>this).debounce = this.options.debounce ? this.options.debounce : this.debounce
        }
        // state.disabled ? this.disable({onlySelf: true, emitEvent: false}) :
        //         this.enable({onlySelf: true, emitEvent: false});
    };
    
    /**
     * @internal
     */
    _getControlProperties() {
        if (this.state) {
            (<{ props: ControlProperties }>this).props = (<FormControlState>this.state).props
        }
    }
    
    /**
     * @internal
     */
    _allControlsDisabled(): boolean {
        return this.disabled;
    }
    
    /** @internal */
    _anyControlsHaveStatus(status: string): boolean {
        return false;
    }
}

